import { useState } from 'react';
import { useClaimAttendance } from '../../hooks/useInventory';
import { useProfile } from '../../hooks/useProfile';
import { getErrorMessage } from '../../utils/errorMessages';
import './AttendanceCheck.css';

export function AttendanceCheck() {
  const [message, setMessage] = useState<string | null>(null);
  const { data: profile } = useProfile();
  const attendanceMutation = useClaimAttendance();
  const isClaiming = attendanceMutation.isPending;

  const attendanceStreak = profile?.attendance_streak || 0;
  const lastAttendanceDate = profile?.last_attendance_date;
  const today = new Date().toISOString().split('T')[0];
  const isCheckedToday = lastAttendanceDate === today;

  // 다음 보너스까지 남은 일수 계산
  const daysUntilBonus = attendanceStreak % 7 === 0 ? 0 : 7 - (attendanceStreak % 7);
  const todayReward = (attendanceStreak + 1) % 7 === 0 ? 5 : 1;

  const handleCheckIn = async () => {
    setMessage(null);
    try {
      const result = await attendanceMutation.mutateAsync();
      const bonusText = result.coinsEarned === 5 ? ' 🎉 보너스!' : '';
      setMessage(
        `출석체크 완료! +${result.coinsEarned} 골드 획득${bonusText} (연속 ${result.attendanceStreak}일)`
      );
    } catch (error) {
      setMessage(getErrorMessage(error));
    }
  };

  // 최근 7일 출석 상태 생성
  const getAttendanceDays = () => {
    const days = [];
    const todayDate = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(todayDate);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      
      let isChecked = false;
      if (lastAttendanceDate) {
        const lastDate = new Date(lastAttendanceDate);
        const checkDate = new Date(dateStr);
        // 마지막 출석일부터 연속으로 체크된 날짜 계산
        if (attendanceStreak > 0) {
          const daysSinceLast = Math.floor(
            (todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          if (daysSinceLast <= attendanceStreak && i <= daysSinceLast) {
            isChecked = true;
          }
        }
      }
      
      days.push({
        date: dateStr,
        dayName,
        isChecked,
        isToday: i === 0,
      });
    }
    
    return days;
  };

  const attendanceDays = getAttendanceDays();

  return (
    <div className="attendance-check">
      <div className="attendance-check__header">
        <h3 className="attendance-check__title">📅 출석체크</h3>
        <div className="attendance-check__streak">
          연속 출석: <span className="attendance-check__streak-number">{attendanceStreak}일</span>
        </div>
      </div>

      {/* 출석 달력 */}
      <div className="attendance-check__calendar">
        {attendanceDays.map((day, index) => (
          <div
            key={day.date}
            className={`attendance-check__day ${day.isChecked ? 'attendance-check__day--checked' : ''} ${day.isToday ? 'attendance-check__day--today' : ''}`}
          >
            <div className="attendance-check__day-name">{day.dayName}</div>
            <div className="attendance-check__day-status">
              {day.isChecked ? '✓' : day.isToday && !isCheckedToday ? '?' : ''}
            </div>
          </div>
        ))}
      </div>

      {/* 보상 정보 */}
      <div className="attendance-check__reward">
        <div className="attendance-check__reward-info">
          {isCheckedToday ? (
            <div className="attendance-check__reward-text">
              오늘 출석체크 완료!
            </div>
          ) : (
            <>
              <div className="attendance-check__reward-text">
                오늘 보상: <span className="attendance-check__reward-amount">+{todayReward} 골드</span>
                {todayReward === 5 && <span className="attendance-check__bonus-badge">보너스!</span>}
              </div>
              {daysUntilBonus > 0 && (
                <div className="attendance-check__next-bonus">
                  보너스까지 {daysUntilBonus}일 남음
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 출석체크 버튼 */}
      <button
        type="button"
        className="attendance-check__button"
        onClick={handleCheckIn}
        disabled={isCheckedToday || isClaiming}
      >
        {isClaiming ? (
          '체크 중...'
        ) : isCheckedToday ? (
          '오늘 출석 완료 ✓'
        ) : (
          '출석체크하기 ✨'
        )}
      </button>

      {message && (
        <div className={`attendance-check__message ${message.includes('보너스') ? 'attendance-check__message--bonus' : ''}`}>
          {message}
        </div>
      )}
    </div>
  );
}

